/* istanbul ignore file */
import _ from 'lodash'
import React, { useEffect, useState, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import configment from '@adc/configment'
import * as graph from '@adc/sdk'
import {
  Button,
  hooks,
  ListingCard,
  useApplicationNotifications,
} from '@adc/ui-adc'
import classnames from 'classnames/bind'
import {
  updateSelectedId,
  updateMapMarkerSelectedId,
  setTriggeredMouseEventFlag,
} from '../../actions/assets'
import { renderHeader } from '../mapHeader'
import ReactGoogleMapApi from '../googleMapApi'
import { ASPECT_RATIO_TOLERANCE } from '../../constants/map'
import {
  getBounds,
  determineBounds,
  fitMarkers,
  shouldMarkersUpdate,
  calculateAspectRatio,
} from '../../util/mapUtils'
import {
  MOBILE_LISTING_CARD,
  trackMapPin,
  trackPropertyClick,
} from '../../services/analytics'
import { isMobile } from '../../util/osDetector'
import cls from './styles.scss'

const cx = classnames.bind(cls)

const MOUSEOVER_TRIGGER_DELAY_MS = 10

const MarkerMap = ({
  assets,
  featureFlags,
  filters,
  updateOpenedAsset,
  updateSelectedId,
  updateMapMarkerSelectedId,
  toggleSaveAsset,
  isMapView,
  onFilterChange,
  setTriggeredMouseEventFlag,
}) => {
  const selectedId = assets?.selectedId
  const assetTotal = assets?.total || 0
  const notifications = useApplicationNotifications()
  const [loaded, setLoaded] = useState(false)
  const [aspectRatio, setAspectRatio] = useState(0)
  const [autoMovingMap, setAutoMovingMap] = useState(false)
  const [mapBoundsUpdated, setMapBoundsUpdated] = useState(false)
  const [hasMobileInitialized, setHasMobileInitialized] = useState(false)
  const [selectedMarkerListingData, setSelectedMarkerListingData] =
    useState(null)
  const [isTablet, setIsTablet] = useState(isMobile())
  const [bounds, setBounds] = useState(null)

  const mapRef = useRef()
  const mouseOverEventRef = useRef()
  const mouseOutEventRef = useRef()
  const prevIsMapView = hooks.usePrevious(isMapView, isMapView)
  const prevAssets = hooks.usePrevious(assets, assets)
  const prevSelectedId = hooks.usePrevious(selectedId, selectedId)
  const prevMapSelectedId = hooks.usePrevious(assets.mapSelectedId)
  const prevFilters = hooks.usePrevious(filters, filters)

  // TODO:
  const searchMapCoordinates = useCallback(
    mapBounds => {
      onFilterChange({ mapBounds })
    },
    [onFilterChange]
  )

  const updateMapBounds = useCallback(
    (fireNewSearch = true) => {
      setMapBoundsUpdated(false)

      // Just fire new searches when needed.
      // (This function is also used to hide the Redo search Button on new search calls)
      if (fireNewSearch) {
        searchMapCoordinates(bounds)
      }
    },
    [searchMapCoordinates, bounds]
  )

  const fitBounds = useCallback(
    bounds => {
      const currentBounds = mapRef.current.getBounds()
      if (JSON.stringify(bounds) === JSON.stringify(currentBounds)) return
      setAutoMovingMap(true)
      mapRef.current.fitBounds(bounds)
    },
    [mapRef.current] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const initializeMobileMap = useCallback(
    (mapBounds, assetsList) => {
      setHasMobileInitialized(true)
      if (Array.isArray(mapBounds) && mapBounds.length > 1) {
        fitBounds(determineBounds(...mapBounds))
      } else {
        fitBounds(fitMarkers(assetsList))
      }
    },
    [fitBounds]
  )

  const onBoundsChanged = useCallback(
    (...args) => {
      if (!mapRef?.current) return
      setBounds(getBounds(mapRef.current.getBounds()))

      if (autoMovingMap) {
        setAutoMovingMap(false)
      } else {
        // If the actual and new aspect ratios are close enough, no new search is needed
        const mapDiv = mapRef.current.getDiv()
        const newAspectRatio = calculateAspectRatio(
          mapDiv.offsetWidth,
          mapDiv.offsetHeight
        )
        const hasAspectRatioChanged = aspectRatio !== newAspectRatio
        const hasAspectRatioExceededTolerance =
          Math.abs(aspectRatio - newAspectRatio) > ASPECT_RATIO_TOLERANCE
        const shouldUpdateMap =
          !hasAspectRatioChanged || hasAspectRatioExceededTolerance
        setAspectRatio(newAspectRatio)

        if (shouldUpdateMap) {
          setMapBoundsUpdated(true)
        }

        setIsTablet(isMobile())
      }
    },
    [autoMovingMap, mapRef, aspectRatio, setIsTablet]
  )

  const onGoogleMapsLoad = useCallback(
    mapInfo => {
      const map = mapInfo?.map
      if (!map || !window.google) return
      mapRef.current = map
      const { mapBounds } = filters

      if (!map && loaded) setLoaded(false)

      if (map && !loaded) {
        const mapDiv = map.getDiv()
        const aspectRatio = calculateAspectRatio(
          mapDiv.offsetWidth,
          mapDiv.offsetHeight
        )

        let bounds

        if (Array.isArray(mapBounds) && mapBounds.length > 1) {
          // The map will move and resize based on bounds passed in
          bounds = determineBounds(...mapBounds)
        } else {
          // The map will move and autosize based on locations of markers
          bounds = fitMarkers(assets.list)
        }
        fitBounds(bounds)
        setLoaded(true)
        setAspectRatio(aspectRatio)
      }
    },
    [mapRef.current, filters, loaded, fitBounds, assets] // eslint-disable-line react-hooks/exhaustive-deps
  )

  hooks.useMountEffect(() => {
    mouseOverEventRef.current = new MouseEvent('mouseover', {
      view: window,
      bubbles: true,
      cancelable: true,
      clientX: 0,
    })
    mouseOutEventRef.current = new MouseEvent('mouseout', {
      view: window,
      bubbles: true,
      cancelable: true,
      clientX: 0,
    })
  })

  useEffect(() => {
    if (typeof document === 'undefined' || prevSelectedId === selectedId) return

    // mouse over for the newly selected
    if (selectedId) {
      const marker = document.getElementById(selectedId)
      if (marker) {
        // This will trigger when the user had hovered over a specific card in the list section
        // We need to trigger a mouse event on the marker with the ID we want.
        // However, since the mouse over listener also triggers scrolling in the card
        // section, we need to mark someway that this is mouse over event is tiggered via JS
        // rather than with the actual mouse. Otherwise we'll trigger unintended scrolls while
        // navigating the cards.
        // Therefore we trigger the flag and delay the mouse event slightly to allow the redux store to catch up
        setTriggeredMouseEventFlag(true)
        setTimeout(
          () => marker.dispatchEvent(mouseOverEventRef.current),
          MOUSEOVER_TRIGGER_DELAY_MS
        )
      }
    }

    // mouse out if no longer selected
    if (prevSelectedId) {
      const marker = document.getElementById(prevSelectedId)
      marker?.dispatchEvent(mouseOutEventRef.current)
    }
  }, [
    selectedId,
    prevSelectedId,
    setTriggeredMouseEventFlag,
    assets.list,
    featureFlags,
  ])

  useEffect(() => {
    if (prevMapSelectedId === assets.mapSelectedId) return

    const listing = assets.list.find(
      item => item?.listing_id === assets.mapSelectedId
    )

    // only trigger hover event track map pin for non mobile environments
    if (!isMobile() && listing) {
      trackMapPin({
        listing,
        isMobileView: false,
        featureFlags,
        isHoverEvent: true,
      })
    }
  }, [assets.mapSelectedId, prevMapSelectedId, featureFlags, assets.list])

  useEffect(() => {
    const isNewSearch =
      assets?.fetchProgress.loading &&
      prevAssets.fetchProgress.loading !== assets?.fetchProgress.loading
    if (isNewSearch) updateMapBounds(false)
  }, [assets, prevAssets, updateMapBounds])

  useEffect(() => {
    const dataLoaded = loaded && !assets.fetchProgress.loading
    if (dataLoaded && window.google) {
      const { mapBounds } = filters
      const nextMarkersList = assets.list
      const currentMarkersList = prevAssets.list

      // Only runs when client first lands on the map screen - which happens from asset(mobile list) view currently.
      if (!hasMobileInitialized && isMapView !== prevIsMapView && isMapView) {
        initializeMobileMap(mapBounds, nextMarkersList)
        return
      }
      const mapViewHasBeenEnabled = isMapView && !prevIsMapView

      // The map will move and autosize based on locations of markers
      const markersHaveChanged = shouldMarkersUpdate(
        currentMarkersList,
        nextMarkersList,
        mapBounds
      )
      if (markersHaveChanged || mapViewHasBeenEnabled) {
        fitBounds(fitMarkers(nextMarkersList))
      }
    }
  }, [
    filters,
    hasMobileInitialized,
    initializeMobileMap,
    isMapView,
    loaded,
    assets,
    prevAssets,
    prevIsMapView,
    fitBounds,
  ])

  const { list: markers, fetchProgress } = assets

  useEffect(() => {
    const getOpenedAssetForMobile = async () => {
      if (
        filters.openedAssetId &&
        filters.openedAssetId !== prevFilters.openedAssetId
      ) {
        /**
         * Note: openedAssetID and selectedAssetId are basically one in the same at the moment,
         * where openedAssetID is used to open the mobile card and
         * selectedAssetId is for highlighting the pin (showing popup)
         * May want to consider consolidating them into one at some point.
         */
        updateSelectedId(filters.openedAssetId)

        /**
         * TODO:
         * This is admittedly ugly as all get out...
         * We need to consolidate to a single data format irrespective of how the data is being loaded.
         * My only concern is where else we are referring to markers depending if flag on or off.
         */
        let existingMarkerListingData = null
        existingMarkerListingData =
          _.find(markers, o => o.listing_id === filters.openedAssetId) || null
        setSelectedMarkerListingData(existingMarkerListingData)
      }
    }

    if (isTablet) {
      getOpenedAssetForMobile()
    }
  }, [
    filters.openedAssetId,
    prevFilters.openedAssetId,
    markers,
    assets,
    isTablet,
    featureFlags,
    updateSelectedId,
  ])

  useEffect(() => {
    renderHeader(mapRef.current, markers.length, assetTotal, featureFlags)
    // Note on deps, eslint wants me to use mapRef but it works better watching mapRef.current
  }, [markers, assetTotal, mapRef.current, featureFlags]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectedMarkerData = useCallback(
    listingId => {
      const marker = document.getElementById(listingId)
      marker?.dispatchEvent(mouseOverEventRef.current)
      if (
        listingId &&
        selectedMarkerListingData?.listing_id &&
        listingId === selectedMarkerListingData?.listing_id
      ) {
        setSelectedMarkerListingData(null)
        updateOpenedAsset(null)
        marker?.dispatchEvent(mouseOutEventRef.current)
      }
    },
    [selectedMarkerListingData, updateOpenedAsset]
  )

  const handleClickIfNotCancelled = (listingData = {}) => {
    if (
      selectedMarkerListingData.listing_status_group !==
      graph.enums.ListingStatusGroup.Canceled
    ) {
      trackPropertyClick({
        listing: selectedMarkerListingData,
        triggeredFrom: MOBILE_LISTING_CARD,
        ...listingData,
      })
    }
  }

  const handleMapClick = useCallback(() => {
    if (assets?.selectedId) {
      const marker = document.getElementById(assets.selectedId)
      marker?.dispatchEvent(mouseOutEventRef.current)
    }
    setSelectedMarkerListingData(null)
    updateOpenedAsset(null)
  }, [updateOpenedAsset, setSelectedMarkerListingData, assets.selectedId])

  const handleSaveIfNotCancelled = (_event, analyticsData = {}) => {
    if (
      selectedMarkerListingData.listing_status_group !==
      graph.enums.ListingStatusGroup.Canceled
    ) {
      const { badges, isReserveMetShown, displayedCurrentBid, displayedERV } =
        analyticsData
      toggleSaveAsset({
        listing: selectedMarkerListingData,
        updateListingSavedCallback: value => {
          setSelectedMarkerListingData({
            ...selectedMarkerListingData,
            primary_property: {
              ...selectedMarkerListingData.primary_property,
              is_currently_saved: value,
            },
          })
        },
        badges,
        isReserveMetShown,
        displayedCurrentBid,
        displayedERV,
        notifications,
      })
    }
  }

  // Only display the assets while they're part of the active search
  const assetsLoaded = !fetchProgress.loading

  // Avoid displaying the map before the initial load is done
  const showMap = assetsLoaded && loaded

  return (
    <div className={cx('full-map-container')} data-elm-id='Asset_Map_View'>
      <div className={cx('map-container')}>
        <div
          className={cx(
            'map-action-buttons-root',
            selectedMarkerListingData && 'with-mobile-marker'
          )}
        >
          {mapBoundsUpdated && (
            <Button
              role='secondary'
              className={cx('redo-search-button')}
              elmId='map_redo_search_button'
              onClick={updateMapBounds}
            >
              Search this Area!
            </Button>
          )}
        </div>
        <ReactGoogleMapApi
          featureFlags={featureFlags}
          channel={configment.get('GOOGLE_MAP_CHANNEL')}
          googleMapsClientId={configment.get('GOOGLE_MAP_CLIENT_ID')}
          onBoundsChanged={onBoundsChanged}
          bounds={bounds}
          onMarkerClick={listingId => {
            updateOpenedAsset(listingId)
            handleSelectedMarkerData(listingId)
          }}
          assetsLoaded={assetsLoaded}
          showMap={showMap}
          markers={markers}
          onGoogleMapsLoad={onGoogleMapsLoad}
          assetTotal={assetTotal}
          map={mapRef?.current}
          onMapClick={handleMapClick}
          updateMapMarkerSelectedId={updateMapMarkerSelectedId}
        />
      </div>
      {selectedMarkerListingData && isTablet && (
        <div className={cx('mobile-card-container')}>
          <ListingCard
            listing={selectedMarkerListingData}
            elmId={`listing_card_${selectedMarkerListingData.listing_id}`}
            linkProps={{
              onClick: handleClickIfNotCancelled,
              href: selectedMarkerListingData.listing_page_path,
            }}
            showV2
            showSave
            onSaveToggle={handleSaveIfNotCancelled}
            viewMode='horizontal'
            isCancelled={
              selectedMarkerListingData.listing_status ===
              graph.enums.ListingStatusGroup.Canceled
            }
            featureFlags={featureFlags}
          />
        </div>
      )}
    </div>
  )
}

MarkerMap.propTypes = {
  assets: PropTypes.object.isRequired,
  featureFlags: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  updateOpenedAsset: PropTypes.func.isRequired,
  toggleSaveAsset: PropTypes.func.isRequired,
  isMapView: PropTypes.bool,
  updateSelectedId: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  updateMapMarkerSelectedId: PropTypes.func.isRequired,
  setTriggeredMouseEventFlag: PropTypes.func.isRequired,
}

const mapActions = {
  updateSelectedId,
  updateMapMarkerSelectedId,
  setTriggeredMouseEventFlag,
}

export default connect(null, mapActions)(MarkerMap)
